import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, interval, Subscription } from 'rxjs';
import { TableService } from './table.service';
import { OrderService } from './order.service';
import { Table, Zone, Order, OrderItem } from '@app/shared/models';
import { TableStatus } from '@app/shared/enums';
import { OrderItemStatus } from '@app/shared/enums';

export interface SimulationMetrics {
  totalCustomers: number;
  customersServed: number;
  customersWaiting: number;
  averageWaitTime: number;
  averageServiceTime: number;
  peakOccupancy: number;
  bottleneckZones: string[];
  tablesUtilization: Map<string, number>;
  doorUtilization: Map<string, number>;
  kitchenUtilization: number;
}

export interface SimulationState {
  isRunning: boolean;
  isPaused: boolean;
  elapsedTime: number;
  startTime: number | null;
  metrics: SimulationMetrics;
}

export interface Customer {
  id: string;
  tableId: string | null;
  orderId: string | null;
  arrivalTime: number;
  orderTime: number | null;
  serviceStartTime: number | null;
  serviceEndTime: number | null;
  exitTime: number | null;
  status: 'waiting' | 'seated' | 'ordering' | 'waiting_food' | 'eating' | 'paying' | 'exiting';
}

@Injectable({
  providedIn: 'root'
})
export class SimulationService {
  private stateSubject = new BehaviorSubject<SimulationState>({
    isRunning: false,
    isPaused: false,
    elapsedTime: 0,
    startTime: null,
    metrics: this.getInitialMetrics()
  });

  public state$: Observable<SimulationState> = this.stateSubject.asObservable();
  
  // Historial de clientes que ya completaron su servicio (para métricas)
  private completedCustomers: Customer[] = [];

  private customers: Map<string, Customer> = new Map();
  private simulationInterval: Subscription | null = null;
  private timeInterval: Subscription | null = null;
  private customerIdCounter = 0;
  private kitchenQueue: Array<{ orderId: string; menuItemId: string; preparationTime: number; startTime: number }> = [];
  private readonly CUSTOMER_ARRIVAL_INTERVAL = 3000; // 3 segundos
  private readonly ORDER_TIME = 5000; // 5 segundos
  private readonly EATING_TIME = 15000; // 15 segundos
  private readonly PAYMENT_TIME = 3000; // 3 segundos

  constructor(
    private tableService: TableService,
    private orderService: OrderService
  ) {}

  private getInitialMetrics(): SimulationMetrics {
    return {
      totalCustomers: 0,
      customersServed: 0,
      customersWaiting: 0,
      averageWaitTime: 0,
      averageServiceTime: 0,
      peakOccupancy: 0,
      bottleneckZones: [],
      tablesUtilization: new Map(),
      doorUtilization: new Map(),
      kitchenUtilization: 0
    };
  }

  startSimulation(zones: Zone[], duration: number = 60000): void {
    const currentState = this.stateSubject.value;
    
    // Si ya hay una simulación corriendo, detenerla primero
    if (currentState.isRunning) {
      this.stopSimulation();
    }

    // Limpiar todo
    this.customers.clear();
    this.completedCustomers = [];
    this.customerIdCounter = 0;
    this.kitchenQueue = [];
    
    // Limpiar suscripciones anteriores
    if (this.simulationInterval) {
      this.simulationInterval.unsubscribe();
      this.simulationInterval = null;
    }
    if (this.timeInterval) {
      this.timeInterval.unsubscribe();
      this.timeInterval = null;
    }

    const startTime = Date.now();
    this.stateSubject.next({
      isRunning: true,
      isPaused: false,
      elapsedTime: 0,
      startTime,
      metrics: this.getInitialMetrics()
    });

    // Simular llegada de clientes
    this.simulationInterval = interval(this.CUSTOMER_ARRIVAL_INTERVAL).subscribe(() => {
      const state = this.stateSubject.value;
      if (!state.isRunning || state.isPaused) return;
      if (Date.now() - startTime >= duration) {
        this.stopSimulation();
        return;
      }

      this.simulateCustomerArrival(zones);
      this.updateSimulation();
      this.processKitchenQueue();
    });

    // Actualizar tiempo transcurrido
    this.timeInterval = interval(1000).subscribe(() => {
      const state = this.stateSubject.value;
      if (!state.isRunning || state.isPaused) return;
      if (state.startTime) {
        const elapsed = Math.floor((Date.now() - state.startTime) / 1000);
        this.stateSubject.next({
          ...state,
          elapsedTime: elapsed
        });
      }
    });
  }

  pauseSimulation(): void {
    const state = this.stateSubject.value;
    if (!state.isRunning) return;

    this.stateSubject.next({
      ...state,
      isPaused: true
    });
  }

  resumeSimulation(): void {
    const state = this.stateSubject.value;
    if (!state.isRunning || !state.isPaused) return;
    
    // Ajustar tiempos de la cola de cocina para compensar la pausa
    const pauseDuration = Date.now() - (state.startTime ? state.startTime + (state.elapsedTime * 1000) : Date.now());
    this.kitchenQueue.forEach(item => {
      item.startTime += pauseDuration;
    });

    this.stateSubject.next({
      ...state,
      isPaused: false,
      startTime: state.startTime ? Date.now() - (state.elapsedTime * 1000) : Date.now()
    });
  }

  stopSimulation(): void {
    if (this.simulationInterval) {
      this.simulationInterval.unsubscribe();
      this.simulationInterval = null;
    }
    
    if (this.timeInterval) {
      this.timeInterval.unsubscribe();
      this.timeInterval = null;
    }

    const state = this.stateSubject.value;
    const finalMetrics = this.calculateFinalMetrics();

    this.stateSubject.next({
      isRunning: false,
      isPaused: false,
      elapsedTime: state.elapsedTime,
      startTime: null,
      metrics: finalMetrics
    });

    // Quitar órdenes de simulación y restaurar mesas desde DB (no persistir cambios del simulador)
    this.orderService.removeSimulationOrders();
    this.tableService.reloadTablesFromDb();
    this.kitchenQueue = [];
  }

  private simulateCustomerArrival(zones: Zone[]): void {
    // Seleccionar una zona aleatoria
    if (zones.length === 0) return;
    const randomZone = zones[Math.floor(Math.random() * zones.length)];
    
    // Buscar una mesa disponible
    this.tableService.tables$.subscribe(tables => {
      const availableTables = tables.filter(t => 
        t.zoneId === randomZone.id && 
        t.status === TableStatus.AVAILABLE
      );

      if (availableTables.length === 0) {
        // No hay mesas disponibles, cliente espera
      const customer: Customer = {
        id: `customer-${this.customerIdCounter++}`,
        tableId: null,
        orderId: null,
        arrivalTime: Date.now(),
        orderTime: null,
        serviceStartTime: null,
        serviceEndTime: null,
        exitTime: null,
        status: 'waiting'
      };
        this.customers.set(customer.id, customer);
        this.updateMetrics();
        return;
      }

      // Asignar mesa (solo en memoria; simulador no persiste)
      const table = availableTables[Math.floor(Math.random() * availableTables.length)];
      this.tableService.updateTableStatusSimulation(table.id, TableStatus.OCCUPIED);

      const customer: Customer = {
        id: `customer-${this.customerIdCounter++}`,
        tableId: table.id,
        orderId: null,
        arrivalTime: Date.now(),
        orderTime: null,
        serviceStartTime: null,
        serviceEndTime: null,
        exitTime: null,
        status: 'seated'
      };

      this.customers.set(customer.id, customer);

      // Simular proceso de pedido después de un tiempo
      setTimeout(() => {
        this.simulateOrder(customer);
      }, this.ORDER_TIME);

      this.updateMetrics();
    }).unsubscribe();
  }

  private simulateOrder(customer: Customer): void {
    if (!customer.tableId) return;

    const customerData = this.customers.get(customer.id);
    if (!customerData) return;

    customerData.status = 'ordering';
    customerData.orderTime = Date.now();
    this.tableService.updateTableStatusSimulation(customer.tableId, TableStatus.ORDER_IN_PROGRESS);

    // Crear orden solo en memoria (simulador no persiste)
    const order = new Order(`order-${Date.now()}`, customer.tableId);
    this.orderService.menuItems$.subscribe(menuItems => {
      if (menuItems.length > 0) {
        const randomItem = menuItems[Math.floor(Math.random() * menuItems.length)];
        const orderItem = new OrderItem(
          `item-${Date.now()}`,
          randomItem.id,
          1,
          randomItem.price
        );
        order.addItem(orderItem);
        
        customerData.orderId = order.id;
        
        const preparationTimeMs = (randomItem.preparationTime || 15) * 1000;
        this.kitchenQueue.push({
          orderId: order.id,
          menuItemId: randomItem.id,
          preparationTime: preparationTimeMs,
          startTime: Date.now()
        });
      }
      this.orderService.createOrderSimulation(order);
    }).unsubscribe();
  }
  
  private processKitchenQueue(): void {
    const now = Date.now();
    const readyItems: string[] = [];
    
    // Verificar qué platos están listos (iterar en reversa para poder eliminar elementos)
    for (let i = this.kitchenQueue.length - 1; i >= 0; i--) {
      const item = this.kitchenQueue[i];
      const elapsed = now - item.startTime;
      if (elapsed >= item.preparationTime) {
        readyItems.push(item.orderId);
        this.kitchenQueue.splice(i, 1);
      }
    }
    
    // Procesar platos listos
    readyItems.forEach(orderId => {
      // Buscar el cliente que tiene esta orden
      this.customers.forEach((customer) => {
        if (customer.orderId === orderId && customer.status === 'ordering') {
          this.simulateFoodReady(customer);
        }
      });
    });
  }

  private simulateFoodReady(customer: Customer): void {
    if (!customer.tableId) return;

    const customerData = this.customers.get(customer.id);
    if (!customerData) return;
    
    // Solo procesar si el cliente está esperando la comida
    if (customerData.status !== 'ordering') return;

    customerData.status = 'waiting_food';
    this.tableService.updateTableStatusSimulation(customer.tableId, TableStatus.SERVING);

    // Simular entrega de comida (tiempo del mesero)
    setTimeout(() => {
      if (!this.customers.has(customer.id)) return; // Verificar que el cliente aún existe
      
      const updatedCustomer = this.customers.get(customer.id);
      if (!updatedCustomer) return;
      
      updatedCustomer.status = 'eating';
      updatedCustomer.serviceStartTime = Date.now();

      // Simular tiempo de comida
      setTimeout(() => {
        if (!this.customers.has(customer.id)) return;
        this.simulatePayment(customer);
      }, this.EATING_TIME);
    }, 2000);
  }

  private simulatePayment(customer: Customer): void {
    if (!customer.tableId) return;

    const customerData = this.customers.get(customer.id);
    if (!customerData) return;

    customerData.status = 'paying';
    this.tableService.updateTableStatusSimulation(customer.tableId, TableStatus.PENDING_PAYMENT);

    setTimeout(() => {
      this.simulateExit(customer);
    }, this.PAYMENT_TIME);
  }

  private simulateExit(customer: Customer): void {
    if (!customer.tableId) return;

    const customerData = this.customers.get(customer.id);
    if (!customerData) return;

    customerData.status = 'exiting';
    customerData.serviceEndTime = Date.now();
    customerData.exitTime = Date.now();

    // Guardar en historial antes de eliminar
    this.completedCustomers.push({ ...customerData });

    this.tableService.updateTableStatusSimulation(customer.tableId, TableStatus.AVAILABLE);
    this.customers.delete(customer.id);

    const state = this.stateSubject.value;
    this.stateSubject.next({
      ...state,
      metrics: {
        ...state.metrics,
        customersServed: this.completedCustomers.length
      }
    });
  }

  private updateSimulation(): void {
    // Actualizar estado de clientes
    this.customers.forEach((customer, id) => {
      // Lógica adicional de actualización si es necesario
    });
  }

  private updateMetrics(): void {
    const state = this.stateSubject.value;
    const waiting = Array.from(this.customers.values()).filter(c => c.status === 'waiting').length;
    const seated = Array.from(this.customers.values()).filter(c => c.status === 'seated' || c.status === 'ordering' || c.status === 'waiting_food' || c.status === 'eating' || c.status === 'paying').length;

    this.stateSubject.next({
      ...state,
      metrics: {
        ...state.metrics,
        totalCustomers: this.completedCustomers.length + this.customers.size,
        customersWaiting: waiting,
        customersServed: this.completedCustomers.length,
        peakOccupancy: Math.max(state.metrics.peakOccupancy, seated + waiting)
      }
    });
  }

  private calculateFinalMetrics(): SimulationMetrics {
    // Combinar clientes completados con clientes actuales
    const allCustomers: Customer[] = [...this.completedCustomers];
    this.customers.forEach(customer => {
      allCustomers.push(customer);
    });
    
    const servedCustomers = allCustomers.filter(c => c.exitTime !== null);
    const totalCustomersProcessed = allCustomers.length;

    let totalWaitTime = 0;
    let totalServiceTime = 0;
    let waitCount = 0;
    let serviceCount = 0;

    servedCustomers.forEach(customer => {
      // Tiempo de espera: desde llegada hasta que empieza a comer
      if (customer.serviceStartTime && customer.arrivalTime) {
        totalWaitTime += customer.serviceStartTime - customer.arrivalTime;
        waitCount++;
      }
      // Tiempo de servicio: desde que empieza a comer hasta que sale
      if (customer.exitTime && customer.serviceStartTime) {
        totalServiceTime += customer.exitTime - customer.serviceStartTime;
        serviceCount++;
      }
    });

    // Calcular utilización de mesas
    const tablesUtilization = new Map<string, number>();
    this.tableService.tables$.subscribe(tables => {
      tables.forEach(table => {
        const utilization = table.status !== TableStatus.AVAILABLE ? 1 : 0;
        tablesUtilization.set(table.id, utilization);
      });
    }).unsubscribe();

    // Identificar cuellos de botella basado en tiempos de espera promedio por zona
    const bottleneckZones: string[] = [];
    const zoneWaitTimes = new Map<string, number[]>();
    
    servedCustomers.forEach(customer => {
      if (customer.serviceStartTime && customer.arrivalTime && customer.tableId) {
        this.tableService.tables$.subscribe(tables => {
          const table = tables.find(t => t.id === customer.tableId);
          if (table && customer.serviceStartTime) {
            const waitTime = customer.serviceStartTime - customer.arrivalTime;
            if (!zoneWaitTimes.has(table.zoneId)) {
              zoneWaitTimes.set(table.zoneId, []);
            }
            zoneWaitTimes.get(table.zoneId)!.push(waitTime);
          }
        }).unsubscribe();
      }
    });
    
    // Identificar zonas con tiempo de espera promedio alto (> 30 segundos)
    zoneWaitTimes.forEach((waitTimes, zoneId) => {
      const avgWait = waitTimes.reduce((a, b) => a + b, 0) / waitTimes.length;
      if (avgWait > 30000) { // 30 segundos
        this.tableService.zones$.subscribe(zones => {
          const zone = zones.find(z => z.id === zoneId);
          if (zone) {
            bottleneckZones.push(zone.name);
          }
        }).unsubscribe();
      }
    });

    return {
      totalCustomers: totalCustomersProcessed,
      customersServed: servedCustomers.length,
      customersWaiting: allCustomers.filter(c => c.status === 'waiting').length,
      averageWaitTime: waitCount > 0 ? totalWaitTime / waitCount : 0,
      averageServiceTime: serviceCount > 0 ? totalServiceTime / serviceCount : 0,
      peakOccupancy: this.stateSubject.value.metrics.peakOccupancy,
      bottleneckZones,
      tablesUtilization,
      doorUtilization: new Map(),
      kitchenUtilization: this.kitchenQueue.length > 0 ? 1 : 0
    };
  }

  getCurrentState(): SimulationState {
    return this.stateSubject.value;
  }
}
